import { useState, useEffect, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/atoms/Button';
import useAccountableBanks from 'hooks/Bank/useAccountableBanks';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import usePusher from 'hooks/usePusher';
import Lottie from 'react-lottie';
import { motion, AnimatePresence } from 'framer-motion';
import type { ActivationStepProps } from '../types';
import OnboardingStepWrapper from '../../components/OnboardingStepWrapper';
import { PaymentInfoCard } from 'components/molecules/InfoCard';
import BankIcon from 'assets/svg/icons/bankIcon';
import { friendlyFormatIBAN } from 'ibantools';
import checkboxReadyAnimation from '../../components/checkbox-ready-animation.json';
import { bankingSignupLogger } from '../../utils/bankingSignupLogger';
import formatMoney from 'utils/formatMoney';
import { getTransaction } from 'api/v1/transactions';

const SUGGESTED_AMOUNT = '€50';
const LOTTIE_ANIMATION_DURATION = 1500; // 1.5 seconds for checkmark animation
const TEXT_DISPLAY_DURATION = 1500; // 1.5 seconds to show amount text before proceeding

/**
 * Step to top up account via bank transfer
 * Shows payment details and listens for incoming transactions from mount.
 * If transfer received → shows success animation → auto-proceeds
 * User can also click Continue to proceed manually without waiting
 */
const TopUpStep: FC<ActivationStepProps> = ({
  onContinue,
  onBack,
  // Wrapper props (automatically injected)
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const { mainAccountableBank } = useAccountableBanks();
  const { data: user } = useGetCurrentCustomerData();
  const channel = usePusher();

  const [showCheckmarkAnimation, setShowCheckmarkAnimation] = useState(false);
  const [showAmountText, setShowAmountText] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState<number | null>(null);

  const hasReceivedTransferRef = useRef(false);
  const phase2TimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const continueTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const accountableBankIban = mainAccountableBank?.bankAccountReference?.IBAN;
  const accountHolderName =
    user?.accountHolderName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    undefined;
  const bic = mainAccountableBank?.bankAccountReference?.BIC;

  // Initial mount logging
  useEffect(() => {
    bankingSignupLogger.debug('[TopUpStep] 🎬 Component mounted', {
      hasChannel: !!channel,
      accountIban: accountableBankIban,
      lottieAnimationDuration: LOTTIE_ANIMATION_DURATION,
      textDisplayDuration: TEXT_DISPLAY_DURATION,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Log state changes
  useEffect(() => {
    bankingSignupLogger.debug('[TopUpStep] 🔄 State changed:', {
      showCheckmarkAnimation,
      showAmountText,
      receivedAmount,
      hasReceivedTransfer: hasReceivedTransferRef.current,
    });
  }, [showCheckmarkAnimation, showAmountText, receivedAmount]);

  // Listen for incoming transaction webhook from component mount
  useEffect(() => {
    if (!channel) {
      bankingSignupLogger.debug('[TopUpStep] No Pusher channel available');
      return;
    }

    bankingSignupLogger.debug(
      '[TopUpStep] 🎧 Started listening for transaction webhooks',
    );

    const handleTransactionReceived = async (data?: {
      data: {
        _id: string;
        communication: string;
      };
      eventId: string;
    }) => {
      bankingSignupLogger.debug(
        '[TopUpStep] 🎉 Transaction webhook received!',
        {
          data,
          alreadyProcessed: hasReceivedTransferRef.current,
        },
      );

      if (hasReceivedTransferRef.current) {
        bankingSignupLogger.debug(
          '[TopUpStep] ⚠️ Already processed a transfer, ignoring',
        );
        return;
      }

      // Mark as received immediately
      hasReceivedTransferRef.current = true;
      bankingSignupLogger.debug('[TopUpStep] ✅ Marking transfer as received');

      // Phase 1: Show checkmark animation immediately
      bankingSignupLogger.debug(
        '[TopUpStep] 🎬 Phase 1: Showing checkmark animation',
      );
      setShowCheckmarkAnimation(true);

      // Fetch transaction details to get the amount
      const transactionId = data?.data?._id;
      if (transactionId) {
        try {
          bankingSignupLogger.debug(
            '[TopUpStep] 📡 Fetching transaction details',
            { transactionId },
          );
          const transaction = await getTransaction(transactionId);
          bankingSignupLogger.debug(
            '[TopUpStep] ✅ Transaction details fetched',
            { amount: transaction.amount },
          );
          if (transaction.amount) {
            setReceivedAmount(Math.abs(transaction.amount));
          }
        } catch (error) {
          bankingSignupLogger.debug(
            '[TopUpStep] ⚠️ Failed to fetch transaction details',
            { error },
          );
          // Continue with animation even if fetch fails
        }
      }

      // Phase 2: After Lottie animation, transition to amount text
      phase2TimeoutRef.current = setTimeout(() => {
        bankingSignupLogger.debug(
          '[TopUpStep] 🎬 Phase 2: Transitioning to amount text',
        );
        setShowCheckmarkAnimation(false);
        setShowAmountText(true);

        // After showing the amount text, proceed to next step
        continueTimeoutRef.current = setTimeout(() => {
          bankingSignupLogger.debug(
            '[TopUpStep] ➡️ Proceeding to next step after animation',
          );
          onContinue();
        }, TEXT_DISPLAY_DURATION);
      }, LOTTIE_ANIMATION_DURATION);
    };

    // Bind to webhook events
    channel.bind('transaction.created', handleTransactionReceived);
    channel.bind('transaction.updated', handleTransactionReceived);

    bankingSignupLogger.debug('[TopUpStep] 📡 Pusher event listeners attached');

    return () => {
      bankingSignupLogger.debug('[TopUpStep] 🔌 Cleaning up Pusher listeners');
      channel.unbind('transaction.created', handleTransactionReceived);
      channel.unbind('transaction.updated', handleTransactionReceived);

      // Clear any pending animation timeouts
      if (phase2TimeoutRef.current) {
        clearTimeout(phase2TimeoutRef.current);
      }
      if (continueTimeoutRef.current) {
        clearTimeout(continueTimeoutRef.current);
      }
    };
  }, [channel, onContinue]);

  // TODO: REMOVE - Debug logger for ALL Pusher events
  useEffect(() => {
    if (!channel || process.env.NODE_ENV !== 'development') return;

    const logAllEvents = (eventName: string) => {
      return (data?: any) => {
        bankingSignupLogger.debug(
          `[TopUpStep] 📨 Pusher Event: "${eventName}"`,
          data,
        );
      };
    };

    // Bind to all common transaction events
    const events = ['transaction.created', 'transaction.updated'];

    bankingSignupLogger.debug(
      '[TopUpStep] 🔍 DEBUG: Attaching listeners to all transaction events:',
      events,
    );

    events.forEach((event) => {
      channel.bind(event, logAllEvents(event));
    });

    return () => {
      bankingSignupLogger.debug(
        '[TopUpStep] 🔍 DEBUG: Cleaning up event loggers',
      );
      events.forEach((event) => {
        channel.unbind(event, logAllEvents(event));
      });
    };
  }, [channel]);

  const handleContinueClick = () => {
    bankingSignupLogger.debug(
      '[TopUpStep] 🚀 Continue button clicked, proceeding to next step',
    );
    onContinue();
  };

  // Show success animation when transfer received during waiting period
  if (showCheckmarkAnimation || showAmountText) {
    bankingSignupLogger.debug('[TopUpStep] 🎨 Rendering: Success Animation', {
      showCheckmarkAnimation,
      showAmountText,
    });
    return (
      <OnboardingStepWrapper
        showProgressBar={showProgressBar}
        progressSegments={progressSegments}
        onProgressSegmentClick={onProgressSegmentClick}
      >
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <AnimatePresence mode="wait">
            {showCheckmarkAnimation && (
              <motion.div
                key="checkmark"
                initial={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              >
                {/* Checkmark animation */}
                <Lottie
                  options={{
                    loop: false,
                    autoplay: true,
                    animationData: checkboxReadyAnimation,
                    rendererSettings: {
                      preserveAspectRatio: 'xMidYMid meet',
                    },
                  }}
                  height={200}
                  width={200}
                />
              </motion.div>
            )}

            {showAmountText && (
              <motion.div
                key="amount-text"
                className="flex flex-col items-center gap-0.5 text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                <p className="text-xl text-gray-400 font-normal leading-7">
                  {t('money_received')}
                </p>
                {receivedAmount !== null && (
                  <p className="text-5xl font-bold text-emerald-600 tracking-wide leading-tight">
                    {formatMoney({
                      amount: receivedAmount,
                      showPositiveSign: true,
                    })}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </OnboardingStepWrapper>
    );
  }

  // Transfer instructions state
  bankingSignupLogger.debug('[TopUpStep] 🎨 Rendering: Payment Details Form');
  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton
      onBack={onBack}
    >
      <PaymentInfoCard
        icon={<BankIcon />}
        title={t('payment_details')}
        className="mb-6"
      >
        <PaymentInfoCard.Item
          title={t('accountable_banking.activation.top_up.amount_label')}
          value={SUGGESTED_AMOUNT}
          hideCopyButton
        />

        {accountableBankIban && (
          <PaymentInfoCard.Item
            title={t('accountable_banking.activation.top_up.iban_label')}
            value={friendlyFormatIBAN(accountableBankIban)}
          />
        )}

        {accountHolderName && (
          <PaymentInfoCard.Item
            title={t('customers.name')}
            value={accountHolderName}
          />
        )}

        {bic && (
          <PaymentInfoCard.Item
            title={t('customer.field.swift')}
            value={bic}
            isLast
          />
        )}
      </PaymentInfoCard>

      <Button
        onClick={handleContinueClick}
        className="w-full"
        color="bank"
        disabled={isUpdatingStep}
      >
        {t('continue')}
      </Button>
    </OnboardingStepWrapper>
  );
};

export default TopUpStep;
